import config from '../config';

// Function to decode JWT token and normalize role
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

// Function to normalize role value
const normalizeRole = (decodedToken) => {
  // Check various possible role claim formats
  const role = decodedToken.role || 
               decodedToken.Role ||
               decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] ||
               decodedToken['role'] ||
               decodedToken.userRole;

  // Convert role to lowercase for consistent comparison
  if (typeof role === 'string') {
    return role.toLowerCase();
  }
  return null;
};

// Function to get user role from API
export const getUserRole = async (token) => {
  try {
    console.log('Fetching user role with token:', token);
    const res = await fetch(`${config.api.url}/user/role`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    console.log('Role response status:', res.status);
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Role fetch error response:', errorText);
      throw new Error('Failed to fetch user role');
    }
    const data = await res.json();
    console.log('User role API response:', data);
    
    // Normalize the role
    let role = data.role?.toLowerCase();
    if (role === 'client_user') role = 'client';
    if (role === 'midwife_user') role = 'midwife';
    
    // Store both role and user ID
    if (role) {
      console.log('Storing user role and ID in localStorage:', role, data.userId);
      localStorage.setItem('userRole', role);
      localStorage.setItem('userId', data.userId);
    }
    
    return role;
  } catch (error) {
    console.error('Error fetching user role:', error);
    return null;
  }
};

export const register = async (email, password, firstName, lastName, phoneNumber, preferred2FAMethod) => {
  try {
    const res = await fetch(`${config.api.authUrl}/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, firstName, lastName, phoneNumber, preferred2FAMethod }),
    });

    const data = await res.json();
    
    // If the response is not ok, it's an error
    if (!res.ok) {
      return { message: data.message || 'Registration failed' };
    }
    
    return data;
  } catch (error) {
    return { message: 'An error occurred during registration' };
  }
};

export const login = async (email, password, forceMethod) => {
  const res = await fetch(`${config.api.authUrl}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, forceMethod }),
  });
  const data = await res.json();
  
  if (data.token) {
    try {
      // Decode token to get user ID
      const decodedToken = decodeToken(data.token);
      if (decodedToken?.sub) {
        localStorage.setItem('userId', decodedToken.sub);
      }

      // Always fetch role from API first
      console.log('Fetching role from API...');
      const role = await getUserRole(data.token);
      console.log('Role from API:', role);
      
      if (role) {
        // Store normalized role
        const normalizedRole = role.toLowerCase();
        localStorage.setItem('userRole', normalizedRole);
        data.userRole = normalizedRole;
        console.log('Stored normalized role:', normalizedRole);
      } else {
        console.error('No role returned from API');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  }
  
  return data;
};

export const verify2FA = async (code, tempToken) => {
  const res = await fetch(`${config.api.authUrl}/verify-2fa`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tempToken}`,
    },
    body: JSON.stringify({ code }),
  });
  const data = await res.json();
  
  if (data.token) {
    const role = await getUserRole(data.token);
    data.userRole = role;
  }
  
  return data;
};

export const validateInvite = async (token) => {
  const res = await fetch(`${config.api.authUrl}/portal-invite/validate?token=${token}`);
  return res.ok;
};

export const acceptInvite = async (token, password) => {
  const res = await fetch(`${config.api.authUrl}/portal-invite/accept`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });
  if (!res.ok) throw new Error(await res.text());
};

export const updateUserDetails = async (userId, firstName, lastName) => {
  const token = localStorage.getItem('velaToken');
  const res = await fetch(`${config.api.url}/user/${userId}`, {
    method: "PUT",
    headers: { 
      "Content-Type": "application/json",
      "Authorization": `Bearer ${token}`
    },
    body: JSON.stringify({ firstName, lastName }),
  });
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText);
  }
  return res.json();
};

